import { Component, Input, OnDestroy } from "@angular/core";
import { Subject, combineLatest } from "rxjs";
import { filter, switchMap, take, takeUntil, finalize } from "rxjs/operators";
import { PayGroup } from "src/app/shared/models/pay-groups";
import { EmployeeAccountSummaryByPayGroupFacadeService } from "../../../services/facade/employee-account-summary-by-pay-group-facade.service";
import { BeneficiaryAccountStatusByPayGroup } from "../../../services/facade/model/BeneficiaryAccountStatusByPayGroup";
import { HttpErrorResponse } from "@angular/common/http";
import { ToastService } from "@shared/services/toast/toast.service";

@Component({
	selector: "app-pay-elective-pay-group-list-items",
	templateUrl: "./pay-elective-pay-group-list-items.component.html",
	styleUrls: ["./pay-elective-pay-group-list-items.component.scss"]
})
export class PayElectivePayGroupListItemsComponent implements OnDestroy {
	private customerId$ = new Subject<string>();
	private paygroups$ = new Subject<PayGroup[]>();
	private destroy$ = new Subject<void>();

	@Input() set selectedCustomerId(value: string) {
		this.customerId$.next(value);
	}

	@Input() set paygroups(value: PayGroup[]) {
		// Don’t set loading here; only set when we actually start the call.
		this.paygroups$.next(value);
	}

	@Input() pageIndex = 0;
	@Input() pageSize: number = 10;

	beneficiaryAccountStatusByPayGroup!: BeneficiaryAccountStatusByPayGroup[];
	loadingBeneficiaryDetails = false;

	constructor(
		private payGroupAccountSummaryFacade: EmployeeAccountSummaryByPayGroupFacadeService,
		private toastService: ToastService
	) {
		combineLatest([this.customerId$, this.paygroups$])
			.pipe(
				filter(([id, groups]) => !!id && !!groups?.length),
				take(1),
				switchMap(([id, groups]) => {
					this.loadingBeneficiaryDetails = true;
					return this.payGroupAccountSummaryFacade.getAccountSummaryByPayGroupGiven(id, groups).pipe(
						takeUntil(this.destroy$),
						finalize(() => {
							this.loadingBeneficiaryDetails = false;
						})
					);
				})
			)
			.subscribe({
				next: result => {
					this.beneficiaryAccountStatusByPayGroup = result;
				},
				error: error => {
					// Always reset loading; finalize already handles this, but safe to keep logic mentally aligned.
					// (Your pipeline has finalize() which guarantees loading flag reset.)
					// this.loadingBeneficiaryDetails = false; // not needed if finalize is present

					// Developer diagnostics
					console.error("[getAccountSummaryByPayGroupGiven] failed", error);

					// Graceful UI default (optional)
					this.beneficiaryAccountStatusByPayGroup = [];

					// User-facing messaging
					if (error instanceof HttpErrorResponse) {
						switch (true) {
							case error.status === 0:
								// Network issue or CORS; status 0 is common when browser can’t reach server.
								this.toastService.showError(
									"Network error while fetching paygroup details. Please check your connection and try again."
								);
								break;
							case error.status >= 500:
								this.toastService.showError(
									"The service is temporarily unavailable. Please try again later."
								);
								break;
							case error.status === 401 || error.status === 403:
								this.toastService.showError(
									"You don’t have permission to view these paygroup details."
								);
								break;
							case error.status === 404:
								this.toastService.showError("No paygroup details found for this customer.");
								break;
							default:
								// 4xx or other specific errors: show backend message if it’s safe & meaningful
								const backendMsg =
									(typeof error.error === "string" && error.error) ||
									error.error?.message ||
									error.message;
								this.toastService.showError(backendMsg || "Unable to fetch paygroup details.");
								break;
						}
					} else {
						// Non-HTTP error thrown by client-side code or Rx pipeline
						this.toastService.showError("Something went wrong while processing paygroup details.");
					}
				}
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
